/**
 * [T68P1-c] Offline Sync Context
 *
 * Detects `navigator.onLine`, queues submissions when offline,
 * auto-flushes and reconciles optimistic IDs when reconnected.
 *
 * Architecture:
 *  - addToQueue(item): appends to localStorage array, updates queueSize state
 *  - flushQueue(): sequentially processes each item via axios, on success removes
 *    from storage and dispatches Redux replace-optimistic actions
 *  - isOnline transitions false→true: flushQueue() is called automatically
 */
import * as React from 'react';
import api, { endpoints } from '@/services/api';
import { useAppDispatch, useAppSelector } from '@/features/hooks';
import {
  replaceOptimisticTransaction,
  removeOptimisticTransaction,
} from '@/features/transactions/transactionsSlice';
import { useToast } from '@/hooks/use-toast';
import type { SyncQueueItem } from '@/lib/types';
import { readPersistedAuthUser } from '@/services/authPersistence';
import {
  getSyncQueueStorageKey,
} from '@/services/offlineQueue';
import {
  getConnectivityState,
  markBrowserOffline,
  markBrowserOnline,
  subscribeConnectivity,
  type BackendReachability,
  type ConnectivityState,
} from '@/services/connectivity';

export { SYNC_QUEUE_KEY } from '@/services/offlineQueue';

interface OfflineSyncContextValue {
  /** True when browser transport is usable and the backend is not known down. */
  isOnline: boolean;
  isBrowserOnline: boolean;
  backendReachable: BackendReachability;
  queueSize: number;
  isFlushing: boolean;
  addToQueue: (item: Omit<SyncQueueItem, 'id' | 'timestamp' | 'retries'>) => void;
}

const OfflineSyncContext = React.createContext<OfflineSyncContextValue>({
  isOnline: true,
  isBrowserOnline: true,
  backendReachable: null,
  queueSize: 0,
  isFlushing: false,
  addToQueue: () => {},
});

export function OfflineSyncProvider({ children }: { children: React.ReactNode }) {
  const [connectivity, setConnectivity] = React.useState<ConnectivityState>(getConnectivityState);
  const [queueSize, setQueueSize] = React.useState(0);
  const [isFlushing, setIsFlushing] = React.useState(false);
  const flushInFlightRef = React.useRef<Promise<void> | null>(null);
  const dispatch = useAppDispatch();
  const currentUserId = useAppSelector((state) => state.auth.user?.id ?? null);
  const currentUserIdRef = React.useRef(currentUserId);
  currentUserIdRef.current = currentUserId;
  const { toast } = useToast();
  const { browserOnline: isBrowserOnline, backendReachable } = connectivity;
  const isOnline = isBrowserOnline && backendReachable !== false;

  // Read only the current authenticated user's queue from storage.
  const readQueue = (userId: string | null): SyncQueueItem[] => {
    if (!userId) return [];
    try {
      const raw = localStorage.getItem(getSyncQueueStorageKey(userId));
      return raw ? (JSON.parse(raw) as SyncQueueItem[]) : [];
    } catch {
      return [];
    }
  };

  const writeQueue = (userId: string, queue: SyncQueueItem[]) => {
    localStorage.setItem(getSyncQueueStorageKey(userId), JSON.stringify(queue));
    setQueueSize(queue.length);
  };

  const isQueueOwnerActive = (ownerUserId: string): boolean => (
    currentUserIdRef.current === ownerUserId
    && readPersistedAuthUser()?.id === ownerUserId
  );

  // Sync queue size whenever the authenticated account changes.
  React.useEffect(() => {
    setQueueSize(readQueue(currentUserId).length);
  }, [currentUserId]);

  /** Map resource + action to axios call */
  const executeItem = async (item: SyncQueueItem): Promise<unknown> => {
    const { resource, action, payload } = item;
    const ep = endpoints as Record<string, Record<string, unknown>>;

    if (resource === 'transactions') {
      if (action === 'CREATE') return api.post(endpoints.transactions.create, payload);
      if (action === 'UPDATE') return api.put(endpoints.transactions.update(payload.id as string), payload);
      if (action === 'DELETE') return api.delete(endpoints.transactions.delete(payload.id as string));
    }
    if (resource === 'budgets') {
      if (action === 'CREATE') return api.post(endpoints.budgets.create, payload);
      if (action === 'UPDATE') return api.put(endpoints.budgets.update(payload.id as string), payload);
      if (action === 'DELETE') return api.delete(endpoints.budgets.delete(payload.id as string));
    }
    if (resource === 'goals') {
      const goalsEp = ep['goals'] as { create: string; update: (id: string) => string; delete: (id: string) => string };
      if (action === 'CREATE') return api.post(goalsEp.create, payload);
      if (action === 'UPDATE') return api.put(goalsEp.update(payload.id as string), payload);
      if (action === 'DELETE') return api.delete(goalsEp.delete(payload.id as string));
    }
    if (resource === 'accounts') {
      if (action === 'CREATE') return api.post(endpoints.accounts.create, payload);
      if (action === 'UPDATE') return api.put(endpoints.accounts.update(payload.id as string), payload);
      if (action === 'DELETE') return api.delete(endpoints.accounts.delete(payload.id as string));
    }
    if (resource === 'categories') {
      const catEp = ep['categories'] as { create: string; update: (id: string) => string; delete: (id: string) => string };
      if (action === 'CREATE') return api.post(catEp.create, payload);
      if (action === 'UPDATE') return api.put(catEp.update(payload.id as string), payload);
      if (action === 'DELETE') return api.delete(catEp.delete(payload.id as string));
    }
    throw new Error(`Unknown resource/action: ${resource}/${action}`);
  };

  const flushQueue = React.useCallback(async () => {
    const ownerUserId = currentUserId;
    if (!ownerUserId) return;

    const queue = readQueue(ownerUserId);
    if (queue.length === 0) return;

    setIsFlushing(true);
    let successCount = 0;
    const updatedQueue = [...queue];

    for (let i = 0; i < updatedQueue.length; i++) {
      if (!isQueueOwnerActive(ownerUserId)) break;

      const item = updatedQueue[i];
      try {
        const response = await executeItem(item);
        // Keep the item queued if the authenticated account changed while the
        // request was in flight; never reconcile it into another account.
        if (!isQueueOwnerActive(ownerUserId)) break;

        const serverData = (response as { data: { data?: unknown } })?.data?.data;

        // Reconcile optimistic ID → real ID for transactions
        if (item.resource === 'transactions' && item.action === 'CREATE' && item.optimisticId && serverData) {
          dispatch(replaceOptimisticTransaction({
            optimisticId: item.optimisticId,
            realItem: serverData as Parameters<typeof replaceOptimisticTransaction>[0]['realItem'],
          }));
        }

        updatedQueue.splice(i, 1);
        i--;
        successCount++;
      } catch (err: unknown) {
        if (!isQueueOwnerActive(ownerUserId)) break;

        const status = (err as { response?: { status?: number } })?.response?.status;
        if (status && status >= 400 && status < 500) {
          // Client error (validation) — remove permanently, show toast
          toast({
            title: 'Sync error',
            description: `Failed to sync ${item.resource} (${item.action}): validation error.`,
            variant: 'destructive',
          });
          updatedQueue.splice(i, 1);
          i--;
          if (item.optimisticId) {
            dispatch(removeOptimisticTransaction(item.optimisticId));
          }
        } else {
          // Network/server error — increment retries
          updatedQueue[i] = { ...item, retries: item.retries + 1 };
          if (updatedQueue[i].retries >= updatedQueue[i].maxRetries) {
            toast({
              title: 'Sync failed',
              description: `Could not sync ${item.resource} after ${item.maxRetries} attempts. Removed.`,
              variant: 'destructive',
            });
            updatedQueue.splice(i, 1);
            i--;
            if (item.optimisticId) {
              dispatch(removeOptimisticTransaction(item.optimisticId));
            }
          }
        }
      }
    }

    writeQueue(ownerUserId, updatedQueue);
    setIsFlushing(false);

    if (successCount > 0) {
      toast({ title: `Synced ${successCount} item${successCount > 1 ? 's' : ''}` });
    }
  }, [currentUserId, dispatch, toast]);

  const requestFlush = React.useCallback((): void => {
    if (flushInFlightRef.current) return;

    const flushPromise = flushQueue().finally(() => {
      flushInFlightRef.current = null;
    });
    flushInFlightRef.current = flushPromise;
  }, [flushQueue]);

  React.useEffect(() => {
    const unsubscribe = subscribeConnectivity(setConnectivity);
    const handleOnline = () => {
      markBrowserOnline();
      requestFlush();
    };
    const handleOffline = () => markBrowserOffline();

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      unsubscribe();
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [requestFlush]);

  React.useEffect(() => {
    if (isBrowserOnline && backendReachable === true) {
      requestFlush();
    }
  }, [backendReachable, isBrowserOnline, requestFlush]);

  const addToQueue = React.useCallback(
    (item: Omit<SyncQueueItem, 'id' | 'timestamp' | 'retries'>) => {
      if (!currentUserId) return;

      const newItem: SyncQueueItem = {
        ...item,
        id: crypto.randomUUID(),
        timestamp: Date.now(),
        retries: 0,
      };
      const queue = readQueue(currentUserId);
      queue.push(newItem);
      writeQueue(currentUserId, queue);
    },
    [currentUserId]
  );

  return (
    <OfflineSyncContext.Provider value={{
      isOnline,
      isBrowserOnline,
      backendReachable,
      queueSize,
      isFlushing,
      addToQueue,
    }}>
      {children}
    </OfflineSyncContext.Provider>
  );
}

export function useOfflineSync(): OfflineSyncContextValue {
  return React.useContext(OfflineSyncContext);
}
